/**
 * Lokalizatsiya: en / uz / ru. Lug'atlar namespace bo'yicha (common + har sahifa).
 * Til: foydalanuvchi profili (User.language) > localStorage > brauzer > en.
 * Tilni almashtirish faqat features/auth/language.ts dagi changeLanguage orqali (UI + profil + orqaga qaytarish).
 */
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import en from "./locales/en";
import ru from "./locales/ru";
import uz from "./locales/uz";
import { setDayjsLocale } from "@/lib/utils";
import { queryClient } from "@/lib/queryClient";

// Sahifa lug'atlari: src/i18n/locales/<namespace>/{en,uz,ru}.ts (har fayl `export default {...}`)
const nsModules = import.meta.glob<{ default: Record<string, unknown> }>("./locales/*/{en,uz,ru}.ts", { eager: true });
const resources: Record<string, Record<string, Record<string, unknown>>> = { en: { ...en }, uz: { ...uz }, ru: { ...ru } };
for (const [path, mod] of Object.entries(nsModules)) {
  const m = path.match(/\.\/locales\/([^/]+)\/(en|uz|ru)\.ts$/);
  if (m) resources[m[2]][m[1]] = mod.default;
}

export const LANGUAGES = [
  { code: "en", label: "English", short: "EN" },
  { code: "uz", label: "O'zbekcha", short: "UZ" },
  { code: "ru", label: "Русский", short: "RU" },
] as const;
export type Language = (typeof LANGUAGES)[number]["code"];

export const STORAGE_KEY = "dh.lang";

export function isLanguage(value: unknown): value is Language {
  return value === "en" || value === "uz" || value === "ru";
}

export function currentLanguage(): Language {
  const lng = i18n.resolvedLanguage ?? i18n.language;
  return isLanguage(lng) ? lng : "en";
}

// Til qayerdan o'zgarmasin (tugma, profil, boshqa tab, ishga tushish) -- hammasi shu yerda, bir xil tartibda.
// Init'dan oldin ulanadi: birinchi render ham to'g'ri dayjs locale bilan bo'ladi.
let initialized = false;
i18n.on("languageChanged", (lng) => {
  const lang: Language = isLanguage(lng) ? lng : "en";
  setDayjsLocale(lang);
  if (typeof document !== "undefined") document.documentElement.lang = lang;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* ixtiyoriy: maxfiy rejim */
  }
  // Serverdan kelgan matnlar (xatolar, ogohlantirishlar, yorliqlar) eski tilda keshda turibdi:
  // ochiq so'rovlar yangi X-Language bilan qayta so'raladi. Profil (auth) kerak emas -- uni changeLanguage boshqaradi.
  if (initialized) void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "auth" });
});

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    supportedLngs: ["en", "uz", "ru"],
    defaultNS: "common",
    interpolation: { escapeValue: false },
    detection: { order: ["localStorage", "navigator"], lookupLocalStorage: STORAGE_KEY, caches: ["localStorage"] },
    returnNull: false,
  })
  .then(() => {
    initialized = true;
    setDayjsLocale(currentLanguage());
  });

/** Faqat UI tilini o'zgartiradi (profilga yozmaydi). Odatda changeLanguage ishlatiladi. */
export function setLanguage(lang: Language) {
  if (lang !== currentLanguage()) void i18n.changeLanguage(lang);
}

// Boshqa tabda til o'zgarsa (localStorage) -- bu tab ham darhol o'tadi
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY && isLanguage(e.newValue)) setLanguage(e.newValue);
  });
}

export default i18n;
